/**
 * Maximum number of closePath operations to accumulate before flushing
 * the canvas path with stroke(). This prevents superlinear rasterization
 * costs when gap data produces thousands of single-point segments.
 */
export const PATH_BATCH_SIZE = 1000;

/**
 * Maximum number of arc operations to accumulate in a scatter dot loop
 * before flushing with fill(). Same rationale as PATH_BATCH_SIZE.
 */
export const ARC_BATCH_SIZE = 1000;

/**
 * The subset of CanvasRenderingContext2D that d3-shape curve implementations
 * call on the context object passed to `.context()`.
 */
type D3PathContext = Pick<
  CanvasRenderingContext2D,
  'moveTo' | 'lineTo' | 'closePath' | 'bezierCurveTo'
>;

/**
 * Creates a lightweight path context wrapper that periodically flushes the
 * accumulated canvas path by calling `stroke() + beginPath()` after a
 * threshold number of `closePath()` calls.
 *
 * d3-shape calls `closePath()` for every single-point segment when gap data
 * produces alternating defined/undefined patterns. Accumulating tens of
 * thousands of sub-paths in one `beginPath()→stroke()` cycle causes
 * superlinear rasterization cost. This wrapper breaks that accumulation
 * into manageable batches.
 *
 * The wrapper only flushes on `closePath()` boundaries, which mark the end
 * of a complete sub-path — so no visual artifacts are produced.
 *
 * @param ctx - The real canvas rendering context (must have stroke styles set before use)
 * @param batchSize - Number of closePath calls before flushing
 */
export function createBatchedStrokeContext(
  ctx: CanvasRenderingContext2D,
  batchSize: number,
): CanvasRenderingContext2D {
  let closePathCount = 0;

  const d3PathContext: D3PathContext = {
    moveTo(x: number, y: number) {
      ctx.moveTo(x, y);
    },
    lineTo(x: number, y: number) {
      ctx.lineTo(x, y);
    },
    bezierCurveTo(
      cp1x: number,
      cp1y: number,
      cp2x: number,
      cp2y: number,
      x: number,
      y: number,
    ) {
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
    },
    closePath() {
      ctx.closePath();
      closePathCount++;
      if (closePathCount >= batchSize) {
        ctx.stroke();
        ctx.beginPath();
        closePathCount = 0;
      }
    },
  };

  // d3-shape only calls the four path methods above. The cast lets callers
  // pass this directly to `.context()` without repeating it at every call site.
  return d3PathContext as unknown as CanvasRenderingContext2D;
}
